import { useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Settings2 } from "lucide-react";

// Shared totals panel — pure math on lineItems + discount + TDS.
// Mirrors the RM Inward calculation semantics 1:1 (GST rule: tax on
// post-discount taxable value, pro-rata line-level discount share).
//
// Props:
//   lineItems      — array of {quantity, rate, tax_id?, tax?}
//   totals         — {discount_type, discount_value, tds_tcs, ...} (state)
//   setTotals      — setter (component pushes computed sub_total/tax/grand back)
//   zohoTaxes      — Zoho tax rows for tax_id → tax_percentage lookup
//   tdsTaxes       — {id, rate, label} rows for the TDS/TCS dropdown
//   onManageTds    — optional callback for the settings icon; hides gear when omitted
//   LOCAL_TAX_RATES — fallback map when Zoho taxes aren't available
export const BillTotalsPanel = ({
  lineItems,
  totals,
  setTotals,
  zohoTaxes = [],
  tdsTaxes = [],
  onManageTds = null,
  LOCAL_TAX_RATES = { NONE: 0, GST_5: 5, GST_12: 12, GST_18: 18, GST_28: 28 },
}) => {
  const zohoLoaded = zohoTaxes && zohoTaxes.length > 0;

  const computed = useMemo(() => {
    let subTotal = 0;
    const lineGross = lineItems.map((item) => {
      const g = Number(item.quantity || 0) * Number(item.rate || 0);
      subTotal += g;
      return g;
    });

    let discountAmount = 0;
    if (totals.discount_type === "percentage") {
      discountAmount = subTotal * (Number(totals.discount_value || 0) / 100);
    } else {
      discountAmount = Number(totals.discount_value || 0);
    }
    if (discountAmount > subTotal) discountAmount = subTotal;
    if (discountAmount < 0) discountAmount = 0;

    let taxTotal = 0;
    lineItems.forEach((item, idx) => {
      const gross = lineGross[idx];
      const lineDiscountShare = subTotal > 0 ? (gross / subTotal) * discountAmount : 0;
      const taxableAmount = gross - lineDiscountShare;
      let taxRate = 0;
      if (zohoLoaded) {
        if (item.tax_id) {
          taxRate = parseFloat(zohoTaxes.find((t) => t.tax_id === item.tax_id)?.tax_percentage) || 0;
        }
      } else {
        taxRate = LOCAL_TAX_RATES[item.tax] || 0;
      }
      taxTotal += taxableAmount * (taxRate / 100);
    });

    const tdsRow = tdsTaxes.find((t) => t.id === totals.tds_tcs);
    const tdsRate = totals.tds_tcs && totals.tds_tcs !== "NONE" ? Number(tdsRow?.rate || 0) : 0;
    const tdsTcsAmount = (subTotal - discountAmount) * (tdsRate / 100);
    const grandTotal = subTotal - discountAmount + taxTotal - tdsTcsAmount;

    return {
      sub_total: subTotal,
      discount_amount: discountAmount,
      tds_tcs_amount: tdsTcsAmount,
      tax_total: taxTotal,
      grand_total: grandTotal,
    };
  }, [lineItems, totals.discount_type, totals.discount_value, totals.tds_tcs, zohoTaxes, tdsTaxes, zohoLoaded, LOCAL_TAX_RATES]);

  const { sub_total, discount_amount, tds_tcs_amount, tax_total, grand_total } = computed;

  useEffect(() => {
    setTotals((prev) => {
      // Guard: if the derived values already match, skip the state
      // write. Prevents the "Maximum update depth exceeded" loop that
      // hits when `setTotals` replaces the object identity on every
      // pass and the parent re-renders every effect dep.
      if (
        prev.sub_total === sub_total
        && prev.discount_amount === discount_amount
        && prev.tds_tcs_amount === tds_tcs_amount
        && prev.tax_total === tax_total
        && prev.grand_total === grand_total
      ) {
        return prev;
      }
      return { ...prev, sub_total, discount_amount, tds_tcs_amount, tax_total, grand_total };
    });
  }, [sub_total, discount_amount, tds_tcs_amount, tax_total, grand_total, setTotals]);

  return (
    <div className="space-y-3 bg-gray-50 p-4 rounded-lg" data-testid="bill-totals-panel">
      <div className="flex justify-between text-sm">
        <span>Sub Total</span>
        <span className="font-mono font-medium" data-testid="totals-subtotal">
          ₹{Number(totals.sub_total || 0).toFixed(2)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm w-20">Discount</span>
        <Select
          value={totals.discount_type || undefined}
          onValueChange={(v) => setTotals({ ...totals, discount_type: v })}
        >
          <SelectTrigger className="w-20 h-8">
            <SelectValue placeholder="%" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="percentage">%</SelectItem>
            <SelectItem value="amount">₹</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={totals.discount_value ?? 0}
          onChange={(e) => setTotals({ ...totals, discount_value: parseFloat(e.target.value) || 0 })}
          onWheel={(e) => e.currentTarget.blur()}
          className="no-spinner w-24 h-8 text-right font-mono"
          data-testid="totals-discount-input"
        />
        <span className="font-mono text-sm ml-auto" data-testid="totals-discount-amount">
          -₹{Number(totals.discount_amount || 0).toFixed(2)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm w-20">TDS</span>
        <Select
          value={totals.tds_tcs || "NONE"}
          onValueChange={(v) => setTotals({ ...totals, tds_tcs: v })}
        >
          <SelectTrigger className="w-44 h-8" data-testid="totals-tds-select">
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="NONE">None</SelectItem>
            {tdsTaxes.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.label || `${t.tax_name} ${t.rate}%`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {onManageTds && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onManageTds}
            title="Manage TDS taxes"
          >
            <Settings2 className="w-4 h-4" />
          </Button>
        )}
        <span className="font-mono text-sm ml-auto">
          -₹{Number(totals.tds_tcs_amount || 0).toFixed(2)}
        </span>
      </div>

      <div className="flex justify-between text-sm">
        <span>Tax Total</span>
        <span className="font-mono" data-testid="totals-tax">
          +₹{Number(totals.tax_total || 0).toFixed(2)}
        </span>
      </div>

      <Separator />

      <div className="flex justify-between text-lg font-bold">
        <span>Grand Total</span>
        <span className="font-mono text-primary" data-testid="totals-grand">
          ₹{Number(totals.grand_total || 0).toFixed(2)}
        </span>
      </div>
    </div>
  );
};

export default BillTotalsPanel;
